import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Waves, Fish, FolderKanban, ShieldCheck, UserCheck, LogIn, LogOut, Loader2, Database, Trophy, Calendar, Users, HelpCircle } from 'lucide-react';

export default function Navbar() {
  const { user, isAuthenticated, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogoutClick = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="w-full bg-gradient-to-r from-emerald-950 via-emerald-900 to-emerald-950 text-white border-b border-emerald-850/80 relative lg:sticky lg:top-0 z-30 shadow-md">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 md:py-4 flex flex-row items-center justify-between gap-2 md:gap-4">
        
        {/* Brand Logo & Title */}
        <Link to="/" className="flex items-center space-x-2 sm:space-x-3.5 hover:opacity-90 transition-opacity shrink-0">
          <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl overflow-hidden border border-amber-400/50 flex items-center justify-center bg-slate-900 shadow-md shrink-0">
            <img src="/carp_arena_logo.jpg" alt="Carp Arena" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-sans font-black text-sm sm:text-lg md:text-xl tracking-tight leading-none uppercase text-amber-400">
                Carp Arena
              </h1>
            </div>
            <p className="text-[9px] sm:text-[10px] text-emerald-200 font-medium hidden md:block">Спортивный карпфишинг</p>
          </div>
        </Link>

        {/* Navigation Links (Conditional Rendering) */}
        <nav className="flex items-center flex-wrap gap-1 sm:gap-2 md:gap-4 justify-center">
          <Link
            to="/"
            title="Каталог водоемов"
            className={`p-2 sm:px-3.5 sm:py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
              isActive('/') 
                ? 'bg-emerald-800 text-white shadow-sm' 
                : 'text-emerald-150 hover:bg-emerald-900/40 hover:text-white'
            }`}
          >
            <Waves className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="hidden sm:inline">Каталог водоемов</span>
          </Link>

          <Link
            to="/competitions"
            title="Соревнования"
            className={`p-2 sm:px-3.5 sm:py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
              isActive('/competitions') 
                ? 'bg-emerald-800 text-white shadow-sm' 
                : 'text-emerald-150 hover:bg-emerald-900/40 hover:text-white'
            }`}
          >
            <Trophy className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="hidden sm:inline">Соревнования</span>
          </Link>

          <Link
            to="/teams"
            title="Рейтинг команд"
            className={`p-2 sm:px-3.5 sm:py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
              isActive('/teams') 
                ? 'bg-emerald-800 text-white shadow-sm' 
                : 'text-emerald-150 hover:bg-emerald-900/40 hover:text-white'
            }`}
          >
            <Users className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="hidden sm:inline">Рейтинг команд</span>
          </Link>

          <Link
            to="/catches"
            title="Каталог уловов"
            className={`p-2 sm:px-3.5 sm:py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
              isActive('/catches') 
                ? 'bg-emerald-800 text-white shadow-sm' 
                : 'text-emerald-150 hover:bg-emerald-900/40 hover:text-white'
            }`}
          >
            <Fish className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="hidden sm:inline">Каталог уловов</span>
          </Link>

          {/* Help / FAQ Link (Opens in new tab) */}
          <Link
            to="/faq"
            target="_blank"
            rel="noopener noreferrer"
            title="Справка и помощь (ЧаВо / FAQ) — Откроется в новой вкладке"
            className={`p-2 sm:px-3 sm:py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
              isActive('/faq') 
                ? 'bg-amber-400 text-slate-950 font-bold shadow-sm' 
                : 'text-emerald-150 hover:bg-emerald-900/40 hover:text-white'
            }`}
          >
            <HelpCircle className="w-4 h-4 text-amber-400 shrink-0" />
            <span className="hidden sm:inline">ЧаВо / Помощь</span>
          </Link>

          {/* Personal Account: only visible/accessible when authenticated */}
          {isAuthenticated && (
            <Link
              to="/cabinet"
              title="Личный кабинет"
              className={`p-2 sm:px-3.5 sm:py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
                isActive('/cabinet') 
                  ? 'bg-emerald-800 text-white shadow-sm' 
                  : 'text-emerald-150 hover:bg-emerald-900/40 hover:text-white'
              }`}
            >
              <UserCheck className="w-4 h-4 text-amber-400 shrink-0" />
              <span className="hidden sm:inline">Личный кабинет</span>
            </Link>
          )}

          {/* Admin panel / Control Dashboard: restricted strictly to app_admin role */}
          {isAuthenticated && (user?.role || '').split(',').map(r => r.trim()).includes('app_admin') && (
            <Link
              to="/admin"
              title="Администрирование"
              className={`p-2 sm:px-3.5 sm:py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
                isActive('/admin') 
                  ? 'bg-amber-50 text-emerald-950 font-bold shadow-sm hover:bg-amber-400' 
                  : 'text-amber-300 hover:bg-amber-500/10 hover:text-amber-200'
              }`}
            >
              <ShieldCheck className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Администрирование</span>
            </Link>
          )}
        </nav>

        {/* Auth status panel */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {isLoading ? (
            <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
          ) : isAuthenticated ? (
            <div className="flex items-center gap-2 sm:gap-3">
              {/* User display details */}
              <div className="text-right hidden md:block">
                <p className="text-xs font-bold text-white">{user?.fullName || user?.email || 'Пользователь'}</p>
                {user?.rank && (
                  <div className="flex items-center justify-end gap-1">
                    <span className="text-[9px] bg-emerald-800 text-amber-400 font-bold px-1 rounded">
                      {user.rank}
                    </span>
                  </div>
                )}
              </div>

              {/* Logout button */}
              <button
                onClick={handleLogoutClick}
                className="bg-emerald-950/75 hover:bg-rose-900/40 border border-emerald-800 hover:border-rose-800 text-emerald-200 hover:text-rose-200 font-bold text-xs p-2 sm:px-3.5 sm:py-2 rounded-xl cursor-pointer transition-all flex items-center gap-1.5"
                title="Выйти из личного кабинета"
              >
                <LogOut className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline">Выйти</span>
              </button>
            </div>
          ) : (
            /* Guest login option */
            <Link
              to="/login"
              title="Войти в кабинет"
              className="bg-amber-400 hover:bg-amber-300 text-amber-950 font-extrabold text-xs px-2.5 py-2 sm:px-4 sm:py-2.5 rounded-xl shadow-md transition-all flex items-center gap-1.5 hover:scale-[1.02]"
            >
              <LogIn className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Войти в кабинет</span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
